import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Language, useTranslation } from '@providers/I18n';

type DirectionProviderProps = {
  children: React.ReactNode;
  direction?: Direction;
};

export type Direction = 'ltr' | 'rtl';
export type DirectionContextValue = {
  direction: Direction;
  setDirection: (value: Direction) => void;
};

const DirectionContext = createContext<DirectionContextValue>({
  direction: 'ltr',
  setDirection: () => ({}),
});

const languageDirectionMap: Record<Language, Direction> = {
  ar: 'rtl',
  en: 'ltr',
};

export const DirectionProvider: React.FC<DirectionProviderProps> = ({
  children,
  direction: directionFromProps,
}) => {
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState<Direction>(
    directionFromProps ?? 'ltr',
  );

  useEffect(() => {
    setDirection(languageDirectionMap[i18n.language as Language]);
  }, [i18n.language]);

  useEffect(() => {
    if (directionFromProps) {
      setDirection(directionFromProps);
    }
  }, [directionFromProps]);

  const value = useMemo(
    () => ({
      direction,
      setDirection,
    }),
    [direction],
  );

  return (
    <DirectionContext.Provider value={value}>
      {children}
    </DirectionContext.Provider>
  );
};

export const useDirection = () => useContext(DirectionContext);
